import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Icon from "../../components/Icon/Icon";
import { signUpUserV2 } from "../../Reducers/actions/authActions";
import { supabase } from "../../../supabase";
import { fetchVerifyCommLink } from "../../Reducers/actions/userActions";
import {
    getEmailValidationMessage,
    getPasswordConfirmationValidationMessage,
    getPasswordValidationMessage,
    getTermsValidationMessage,
    getUsernameValidationMessage,
    PASSWORD_REQUIREMENTS_MESSAGE,
} from "./signUpValidation";
import { useKeyboardDetectionNoRoot } from "../../../helpers/hooks";
import './AuthCommLink.scss'
import { useSignupValidation } from "./useSignupValidation";

export default function SignUpByCommLink() {
    const [password, setPassword] = useState("");
    const [pwdConfirm, setPwdConfirm] = useState("")
    // const [name, setName] = useState("");
    // const [surname, setSurname] = useState("");
    const [username, setUsername] = useState("");
    const [loading, setLoading] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [error, setError] = useState(null)
    const [showPwd, setShowPwd] = useState(false)
    const [invitedBy, setInvitedBy] = useState("")
    const [email, setEmail] = useState("")
    const [isEmailSent, setIsEmailSent] = useState(false)
    const [senderId, setSenderId] = useState(null)
    const [step, setStep] = useState(0)
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Hook to detect mobile keyboard and adjust viewport
    const { keyboardVisible } = useKeyboardDetectionNoRoot();

    useEffect(() => {
        supabase.auth.signOut()
        const code = searchParams.get("code")
        if (code) {
            fetchVerifyCommLink(code).then(({ success, message, data }) => {
                if (!success) {
                    setError(message)
                    setTimeout(() => {
                        navigate('/login')
                    }, 3000)
                }
                setSenderId(data.sender_id)
                if(data.sender?.firstName && data.sender?.lastName) setInvitedBy(`${data.sender?.firstName} ${data.sender?.lastName}`)
                    else setInvitedBy("the HYK Platform")
            })
        }
        else {
            navigate("/")
        }
    }, [navigate, searchParams])

    const stepFieldsMap = useMemo(() => ({
        0: ["email"],
        1: ["username"],
        2: ["password", "pwdConfirm", "termsAccepted"],
    }), []);

    const getFieldMessage = useCallback((fieldName) => {
        if (fieldName === "email") return getEmailValidationMessage(email);
        if (fieldName === "username") return getUsernameValidationMessage(username);
        if (fieldName === "password") return getPasswordValidationMessage(password);
        if (fieldName === "pwdConfirm") return getPasswordConfirmationValidationMessage(password, pwdConfirm);
        if (fieldName === "termsAccepted") return getTermsValidationMessage(termsAccepted);
        return null;
    }, [email, password, pwdConfirm, termsAccepted, username]);

    const {
        fieldErrors,
        touchedFields,
        attemptedSubmit,
        setFieldErrors,
        setTouchedFields,
        setAttemptedSubmit,
        getStepFields,
        validateFields,
        handleFieldBlur,
        showFieldError,
        markFieldsTouched,
    } = useSignupValidation({
        step,
        stepFieldsMap,
        getFieldMessage,
    });


    const handleSignUp = async () => {
        const isValid = validateFields(getStepFields(2));
        if (!isValid) return;

        setLoading(true)
        signUpUserV2({ password, passwordVerification: pwdConfirm, email, username, sender_id: senderId }).then(({ success, message }) => {
            if (success) {
                setIsEmailSent(true)
            } else {
                //  'For security purposes, you can only request this after 35 seconds.'
                //  Endpoint is returning DB error
                //  setError(message)
                setError(message || "Email already in use try again in 35 seconds")
            }
            setLoading(false)

        })
    }

    const handleChangeStep = () => {
        setAttemptedSubmit(true);
        const currentFields = getStepFields(step);
        const isValid = validateFields(currentFields);

        if (!isValid) {
            markFieldsTouched(currentFields);
            return;
        }

        if (step === 0) {
            setAttemptedSubmit(false);
            setStep(1);
            return;
        }

        if (step === 1) {
            setAttemptedSubmit(false);
            setStep(2);
            return;
        }

        if (step === 2) handleSignUp();
    }

    useEffect(() => {
        return () => localStorage.removeItem("email")
    }, [])

    return (
        <div
            className="auth-bg pt-12 p-fixed"
            style={{ width: '100dvw', top: 0, left: 0 }}
        >
            {!keyboardVisible && !isEmailSent && <div className="d-flex aic jcc my-40 pb-24">
                <Icon alias={"hyk"} width={48} color="#FFF" />
            </div>}
            <div className={`auth-container pb-40 h-100`}>
                {isEmailSent ? (
                    <div className="d-flex f-column aic mt-40 pt-40">
                        <div className="check-email-icon">
                            <Icon alias={"check-your-email"} width={200} height={200}/>
                        </div>

                        <p className="fg-black fs-sm2 fw-medium-bold mb-12">Verify your email address</p>
                        <p className="fg-hyk-medgrey ta-center">We sent an email to {localStorage.getItem('email') || 'your email address'}, tap the link you received to confirm your email address. Take a look at that spam folder if you can't find it.</p>
                        <p 
                          className="email-link"
                          onClick={() => {
                            setStep(0);
                            setEmail('');
                            setIsEmailSent(false);
                            setFieldErrors({});
                            setTouchedFields({});
                            setAttemptedSubmit(false);
                          }}
                        >
                          Use a different email address.
                        </p>
                    </div>
                ) : (
                    <>
                        {step > 0 && <span className="p-absolute back-icon" onClick={() => setStep(prev => prev - 1)}>
                            <Icon alias={"chevron-left"} width={24} color="#000" className="ml-16" />
                        </span>}
                        {step === 0 && <h2 className="auth-title fg-crea fw-bold mb-8">WELCOME TO HYK</h2>}
                        {step === 1 && <p className="auth-title fs-sm2 fg-crea fw-bold mb-8">Add a username</p>}

                        {step === 2 && <p className="auth-title fs-sm2 fg-crea fw-bold mb-8">Create a password</p>}
                        {step === 0 && <p className="fg-crea mt-0 mb-40">You have been invited by {invitedBy?invitedBy:"the HYK Platform"}</p>}

                        {error && <p className="error-message">{error?.length > 0 ? error : 'There was en error, try again later.'}</p>}
                        <div className="auth-form">
                            {/* <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="First Name"
                                className="mb-16 input-main"
                            />
                            <input
                                type="text"
                                value={surname}
                                onChange={(e) => setSurname(e.target.value)}
                                placeholder="Last Name"
                                className="mb-16 input-main"
                            /> */}
                            {step === 0 && <Fragment>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => {
                                        const nextEmail = e.target.value;
                                        setEmail(nextEmail);
                                        if (touchedFields.email) {
                                            setFieldErrors((prev) => ({ ...prev, email: getEmailValidationMessage(nextEmail) }));
                                        }
                                    }}
                                    onBlur={() => handleFieldBlur("email")}
                                    placeholder="Email"
                                    className={`mb-8 input-main ${showFieldError("email") ? "input-error" : ""}`}
                                />
                                {showFieldError("email") && <p className="inline-field-error mt-0 mb-12">{fieldErrors.email}</p>}
                            </Fragment>}
                            {step === 2 && <Fragment>
                                <div className="p-relative">
                                    <input
                                        type={showPwd ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => {
                                            const nextPassword = e.target.value;
                                            setPassword(nextPassword);
                                            if (touchedFields.password || touchedFields.pwdConfirm) {
                                                setFieldErrors((prev) => ({
                                                    ...prev,
                                                    password: getPasswordValidationMessage(nextPassword),
                                                    pwdConfirm: getPasswordConfirmationValidationMessage(nextPassword, pwdConfirm),
                                                }));
                                            }
                                        }}
                                        onBlur={() => handleFieldBlur("password")}
                                        placeholder="Password"
                                        className={`mb-8 input-main w-full ${showFieldError("password") ? "input-error" : ""}`}
                                    />
                                    <Icon alias={"show-password"} width={24} color="#777777" className={'show-icon'} onClick={() => setShowPwd(prev => !prev)} />
                                </div>
                                {showFieldError("password") && <p className="inline-field-error mt-0 mb-12">{fieldErrors.password}</p>}
                                <p className="fg-black mb-16 fw-medium fs-real mt-0">{PASSWORD_REQUIREMENTS_MESSAGE}</p>

                                <div className="p-relative">
                                    <input
                                        type={showPwd ? "text" : "password"}
                                        value={pwdConfirm}
                                        onChange={(e) => {
                                            const nextPasswordConfirm = e.target.value;
                                            setPwdConfirm(nextPasswordConfirm);
                                            if (touchedFields.pwdConfirm) {
                                                setFieldErrors((prev) => ({
                                                    ...prev,
                                                    pwdConfirm: getPasswordConfirmationValidationMessage(password, nextPasswordConfirm),
                                                }));
                                            }
                                        }}
                                        onBlur={() => handleFieldBlur("pwdConfirm")}
                                        placeholder="Confirm password"
                                        className={`mb-8 input-main w-full ${showFieldError("pwdConfirm") ? "input-error" : ""}`}
                                    />
                                    <Icon alias={"show-password"} width={24} color="#777777" className={'show-icon'} onClick={() => setShowPwd(prev => !prev)} />
                                </div>
                                {showFieldError("pwdConfirm") && <p className="inline-field-error mt-0 mb-12">{fieldErrors.pwdConfirm}</p>}
                                <div className="d-flex aic mb-24">
                                    <input
                                        type="checkbox"
                                        id="terms"
                                        name="terms"
                                        checked={termsAccepted}
                                        onChange={() => {
                                            const nextValue = !termsAccepted;
                                            setTermsAccepted(nextValue);
                                            if (touchedFields.termsAccepted) {
                                                setFieldErrors((prev) => ({ ...prev, termsAccepted: getTermsValidationMessage(nextValue) }));
                                            }
                                        }}
                                        onBlur={() => handleFieldBlur("termsAccepted")}
                                    />
                                    <label htmlFor="terms" className="ml-8 fs-xs">I agree to the <a href="/terms" target="_blank" className="link-btn">Terms & Conditions</a> and <a href="/privacy" target="_blank" className="link-btn">Privacy Policy</a>.</label>
                                </div>
                                {showFieldError("termsAccepted") && <p className="inline-field-error mt-0 mb-12">{fieldErrors.termsAccepted}</p>}
                            </Fragment>}
                            {step === 1 && <Fragment>
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => {
                                        const nextUsername = e.target.value.toUpperCase();
                                        setUsername(nextUsername);
                                        if (touchedFields.username) {
                                            setFieldErrors((prev) => ({ ...prev, username: getUsernameValidationMessage(nextUsername) }));
                                        }
                                    }}
                                    onBlur={() => handleFieldBlur("username")}
                                    placeholder="USERNAME"
                                    className={`mb-8 input-main ${showFieldError("username") ? 'input-error' : ''}`}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="characters"
                                    spellCheck="false"
                                    data-form-type="other"
                                    data-lpignore="true"
                                    data-1p-ignore="true"
                                />
                                {showFieldError("username") && <p className="inline-field-error mt-0 mb-12">{fieldErrors.username}</p>}
                                <p className="fg-black mb-40 fs-real fw-medium mt-0">Usernames are at least 3 characters long, all caps, and can contain letters, numbers, and underscores only.</p>

                            </Fragment>
                            }
                            <div className='p-fixed bg-white auth-footer mt-24 pb-24' style={{ bottom: '0', left: '0' }}>
                                {step === 0 && <Fragment>
                                    <button className="btn red-btn btn-full-v fs-real d-flex aic jcc p-relative" onClick={handleChangeStep} disabled={loading}>
                                        {loading ? "Signing Up..." : "Sign Up"}
                                    </button>
                                    <div className="mb-24">
                                        <p className="fg-crea fs-xxs fw-medium ta-center">ALREADY HAVE AN ACCOUNT? <a href="/login" className="fg-red-pink fw-medium td-underline">LOGIN</a></p>
                                    </div>
                                </Fragment>
                                }
                                {step > 0 && <Fragment>
                                    <button className="btn red-btn btn-full-v d-flex fs-real aic jcc p-relative mb-24" onClick={handleChangeStep} disabled={loading}>
                                        {"Continue"}
                                    </button>
                                </Fragment>}
                            </div>
                        </div>
                    </>)}
            </div>

        </div>
    );
}
